import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';

import { GroupLabelForm } from '../GroupLabel';
import { MemoConfirmDialog } from '../MemoConfirmDialog';
import { suggestGroupLabel } from '../utils/suggest-group-label.util';
import type { GroupLabelValues } from '../GroupLabel';
import type { GroupOption } from '../MoveToGroup';
import type { GroupMutations } from '../types/props.type';
import type { SubmitHandler } from '../types/submit-handler.type';

import { useDirectMutationCallbacks } from './useDirectMutationCallbacks.hook';

type GroupAction =
  | Readonly<{ kind: 'create' }>
  | Readonly<{ kind: 'remove'; group: GroupOption }>
  | Readonly<{ kind: 'rename'; group: GroupOption }>;

interface Params {
  readonly askMemoBeforeSubmit: boolean;
  readonly groups: readonly GroupOption[];
  readonly mutations: GroupMutations | undefined;
  readonly queryKeysToInvalidate: readonly (readonly string[])[];
  readonly serviceUserId: Id;
}

// Group reordering (and moving usages across groups) now goes through the
// single two-level ReorderDrawer (see docs/spec/00-project-spec.md "Feature:
// Total per group" - "Single reorder drawer"), not through this hook.
export const useServiceUsagesGroupActions = ({
  askMemoBeforeSubmit,
  groups,
  mutations,
  queryKeysToInvalidate,
  serviceUserId,
}: Readonly<Params>) => {
  const [groupAction, setGroupAction] = useState<GroupAction | null>(null);

  const closeDialog = useCallback(() => {
    setGroupAction(null);
  }, []);

  const buildCallbacks = useDirectMutationCallbacks({
    onSuccess: closeDialog,
    queryKeysToInvalidate,
  });

  const handleLabelSubmit = useCallback<SubmitHandler<GroupLabelValues>>(
    ({ values, memos, formikHelpers: { setSubmitting }, setFormError }) => {
      setFormError(null);

      const label = values.label.trim();
      const { isOptional } = values;
      const versionData = memoForm2ApiMapper(memos);

      if (groupAction?.kind === 'rename') {
        mutations?.updateGroup(
          {
            isOptional,
            label,
            serviceUsagesGroupId: groupAction.group._id,
            serviceUserId,
            versionData,
          },
          buildCallbacks({
            setFormError,
            setSubmitting,
            successMessage: 'Le groupe a bien été modifié.',
          }),
        );

        return undefined;
      }

      mutations?.createGroup(
        { isOptional, label, serviceUserId, versionData },
        buildCallbacks({
          setFormError,
          setSubmitting,
          successMessage: 'Le groupe a bien été créé.',
        }),
      );

      return undefined;
    },
    [buildCallbacks, groupAction, mutations, serviceUserId],
  );

  const handleRemoveSubmit = useCallback<SubmitHandler<object>>(
    ({ memos, formikHelpers: { setSubmitting }, setFormError }) => {
      setFormError(null);

      if (groupAction?.kind !== 'remove') {
        return undefined;
      }

      mutations?.removeGroup(
        {
          serviceUsagesGroupId: groupAction.group._id,
          serviceUserId,
          versionData: memoForm2ApiMapper(memos),
        },
        buildCallbacks({
          setFormError,
          setSubmitting,
          successMessage: 'Le groupe a bien été supprimé.',
        }),
      );

      return undefined;
    },
    [buildCallbacks, groupAction, mutations, serviceUserId],
  );

  const renderDialog = (action: GroupAction): ReactNode => {
    if (action.kind === 'remove') {
      return (
        <MemoConfirmDialog<object>
          askMemoBeforeSubmit={askMemoBeforeSubmit}
          description={`Le groupe « ${action.group.label ?? EMPTY_STRING} » sera supprimé.`}
          id="remove-service-usages-group"
          initialValues={EMPTY_OBJECT}
          isDialogOpen
          onCloseDialog={closeDialog}
          onSubmit={handleRemoveSubmit}
          submitLabel="Supprimer"
          title="Supprimer le groupe"
        />
      );
    }

    return (
      <GroupLabelForm
        askMemoBeforeSubmit={askMemoBeforeSubmit}
        initialIsOptional={
          action.kind === 'rename' && !!action.group.isOptional
        }
        initialLabel={
          action.kind === 'rename'
            ? (action.group.label ?? EMPTY_STRING)
            : suggestGroupLabel(groups.map((group) => group.label))
        }
        isDialogOpen
        mode={action.kind}
        onCloseDialog={closeDialog}
        onSubmit={handleLabelSubmit}
      />
    );
  };

  const dialog = groupAction
    ? createPortal(renderDialog(groupAction), document.body)
    : null;

  const handleStartCreateGroup = useCallback(() => {
    setGroupAction({ kind: 'create' });
  }, []);

  const handleStartRemoveGroup = useCallback((group: GroupOption) => {
    setGroupAction({ kind: 'remove', group });
  }, []);

  const handleStartRenameGroup = useCallback((group: GroupOption) => {
    setGroupAction({ kind: 'rename', group });
  }, []);

  return {
    dialog,
    handleStartCreateGroup,
    handleStartRemoveGroup,
    handleStartRenameGroup,
  };
};
